import { fetcher } from "common/api/Fetcher";
import { downloadFile } from "common/helpers/File";
import { objectToFormData } from "common/helpers/FormData";
import { EstimateTemplateRecord, IEstimateTemplateData } from "common/records/EstimateTemplate";
import { IMetaPaginationData } from "common/records/MetaPagination";
import { EstimateTemplateParamsRecord } from "common/records/index/EstimateTemplateParams";
import { toParams } from "common/records/index/QueryParamsBase";

export interface IImportEstimateTemplateResponse {
  message: string;
  success_count: number;
}

export interface IEstimateTemplateIndexResponse {
  estimate_templates: IEstimateTemplateData[];
  meta: { pagination: IMetaPaginationData };
}

export interface IEstimateTemplateResponse {
  estimate_template: IEstimateTemplateData;
}

const url = "api/v1/orgs/:org_id/estimate_templates";

const orgUrl = (orgId: number): string => url.replace(":org_id", orgId.toString());

/**
 * Serializes a template record to the create/update payload. `template` content is included ONLY when
 * `includeTemplate` is set (create + overwrite) — a metadata-only update (rename / folder move /
 * reorder) must NOT re-send the content, because the record's hydration is a strict allowlist that
 * drops unmodeled fields and re-sending would overwrite the server's content with a stripped copy.
 */
export const cleanProps = (
  record: EstimateTemplateRecord,
  { includeTemplate = false }: { includeTemplate?: boolean } = {},
): Record<string, unknown> => {
  const data = record.toJS() as IEstimateTemplateData;
  const params: Record<string, unknown> = {
    name: data.name,
    kind: data.kind ?? "standard",
  };
  if (data.folder_id != null && data.folder_id > 0) params.folder_id = data.folder_id;
  if (data.sort_order != null) params.sort_order = data.sort_order;
  if (includeTemplate) params.template = data.template;
  return params;
};

/**
 * List of the org's estimate templates for the picker (Name / Updated On). The index already
 * returns each row's heavy `template` payload, so the apply flow reads it straight off the
 * list-hydrated Record — no per-selection `show` fetch.
 * V1 lists standard templates only (the index returns standard-kind by default).
 */
export const list = (
  orgId: number,
  queryParams: EstimateTemplateParamsRecord,
): Promise<IEstimateTemplateIndexResponse> => {
  const parameters = toParams(queryParams);
  const actionUrl = url.replace(":org_id", orgId.toString());
  return fetcher.get<IEstimateTemplateIndexResponse>(actionUrl, parameters);
};

export const create = (orgId: number, record: EstimateTemplateRecord): Promise<IEstimateTemplateResponse> => {
  return fetcher.post<IEstimateTemplateResponse>(orgUrl(orgId), cleanProps(record, { includeTemplate: true }));
};

/**
 * Updates a template. `options.includeTemplate` switches between a content-replacing overwrite and a
 * metadata-only update (rename / folder move / reorder). `options.deleteFiles` clears the template's
 * attached files before the content is replaced (overwrite path), so re-uploaded files don't stack.
 */
export const update = (
  orgId: number,
  record: EstimateTemplateRecord,
  options: { includeTemplate?: boolean; deleteFiles?: boolean } = {},
): Promise<IEstimateTemplateResponse> => {
  const actionUrl = fetcher.joinUrls(orgUrl(orgId), record.id.toString());
  const body = cleanProps(record, { includeTemplate: options.includeTemplate });
  if (options.deleteFiles) body.delete_files = true;
  return fetcher.patch<IEstimateTemplateResponse>(actionUrl, body);
};

export const destroy = (orgId: number, id: number): Promise<void> => {
  const actionUrl = fetcher.joinUrls(orgUrl(orgId), id.toString());
  return fetcher.delete(actionUrl);
};

// Call for importing org estimate templates
export const importEstimateTemplate = async (
  orgId: number,
  document: File,
): Promise<IImportEstimateTemplateResponse> => {
  const data = objectToFormData({ file: document });
  const actionUrl = fetcher.joinUrls(url.replace(":org_id", orgId.toString()), "import");

  return await fetcher.post(actionUrl, data, { formData: true });
};

export const exportEstimateTemplate = async (orgId: number): Promise<void> => {
  const actionUrl = url.replace(":org_id", orgId.toString()) + ".csv";
  const filename = `occ_estimate_template_export_${orgId}_${new Date().toISOString()}.csv`;
  await downloadFile(actionUrl, filename);
  return;
};
